import type { InferResponseType } from "hono/client";
import useSWR from "swr";
import { apiClient } from "../rpc";

const meOrders = apiClient.me.orders;

/**
 * SWR のキャッシュキー。`$url()` は rpc を mock するテストでは存在しないため、URL に依存しない
 * 固定文字列にしている (一覧は 1 種類しかないのでこれで一意)。
 */
const ORDERS_KEY = "mypage:me/orders";

export type OrderSummary = InferResponseType<typeof meOrders.$get, 200>["orders"][number];

const fetchOrders = async (): Promise<OrderSummary[]> => {
  const res = await meOrders.$get();
  if (res.status === 200) {
    const data = await res.json();
    return data.orders;
  }
  throw new Error(`HTTP ${res.status}`);
};

type Result = {
  orders: OrderSummary[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

/**
 * 注文一覧を取得する。SWR でキャッシュするため、詳細ページなどから戻ってきても直前の一覧を
 * そのまま表示したまま裏で再取得する (毎回ローディングに切り替わらない)。エラー時は自動リトライ
 * せず、UI 側の「再試行」ボタン (refetch) に委ねる。
 */
export const useOrders = (): Result => {
  const { data, error, isLoading, mutate } = useSWR<OrderSummary[], Error>(
    ORDERS_KEY,
    fetchOrders,
    {
      shouldRetryOnError: false,
    },
  );

  return {
    orders: data ?? null,
    loading: isLoading,
    error: error ? error.message : null,
    refetch: async () => {
      await mutate();
    },
  };
};
